import { booleanAttribute } from "#ce/utils/props";

export const DEFAULT_ROWS = 3;

// The smallest row count a textarea can show; `rows` and `max-rows` ignore anything below it.
export const MIN_ROWS = 1;

// We had bugs in the past, where the props were mutated. Apply Object.freeze() to prevent this.
//
// `value` is absent on purpose and is documented in the story: the element owns a live `value`
// property, which a declared prop would shadow. See `#ce/composables/useTextModel.js`.
export const props = Object.freeze({
  /**
   * Sets the textarea’s placeholder text.
   *
   * @type {String}
   * @default undefined
   */
  placeholder: {
    type: String,
    default: undefined,
  },
  /**
   * Number of visible text rows, and the height the field never drops below. Rounded down to a
   * whole number; anything below 1, or unparseable, falls back to the default.
   *
   * @type {Number|String}
   * @default 3
   * @storybook-control number
   */
  rows: {
    type: [Number, String],
    default: DEFAULT_ROWS,
  },
  /**
   * Grow the height with the content instead of scrolling.
   *
   * @type {Boolean|String}
   * @default false
   */
  autoGrow: booleanAttribute(),
  /**
   * Caps `autoGrow` at this many rows; content beyond it scrolls. Ignored without `autoGrow`, and
   * never caps below `rows`. Rounded down to a whole number; anything below 1, or unparseable,
   * leaves the growth uncapped.
   *
   * @type {Number|String}
   * @default undefined
   * @storybook-control number
   */
  maxRows: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * Visible width in average character widths. The field spans its container unless this is set,
   * in which case the field takes the width the column count asks for.
   *
   * @type {Number|String}
   * @default undefined
   * @storybook-control number
   */
  cols: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * How the text wraps as it is displayed. `off` turns wrapping off and scrolls horizontally
   * instead. The submitted text is what the field holds either way — the native `hard` value,
   * which folds the displayed line breaks into the submitted value, is not supported and is
   * ignored rather than passed through.
   *
   * @type {String}
   * @default undefined
   * @values soft, off
   */
  wrap: {
    type: String,
    validator: (value) => value === undefined || ["soft", "off"].includes(value),
    default: undefined,
  },
  /**
   * Longest text the field accepts, as a non-negative integer; anything else is ignored, the way
   * the platform ignores it. Typing stops at the limit; a longer seeded value fails validation as
   * `tooLong` once the user edits it, the way a native `<textarea>` does.
   *
   * @type {Number|String}
   * @default undefined
   * @storybook-control number
   */
  maxlength: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * Shortest text the field accepts, as a non-negative integer; anything else is ignored, the way
   * the platform ignores it. A shorter text fails validation as `tooShort` once the user has
   * edited the field; an empty field is `valueMissing` or valid, never `tooShort`.
   *
   * @type {Number|String}
   * @default undefined
   * @storybook-control number
   */
  minlength: {
    type: [Number, String],
    default: undefined,
  },
  /**
   * Removes the ability to click or target the textarea.
   *
   * @type {Boolean|String}
   * @default false
   */
  disabled: booleanAttribute(),
  /**
   * Removes the ability to edit the textarea’s value.
   *
   * @type {Boolean|String}
   * @default false
   */
  readonly: booleanAttribute(),
  /**
   * Sets the textarea’s required attribute. Presence-based, the way the platform reads a boolean
   * attribute: any value other than `false` constrains the field.
   *
   * @type {Boolean|String}
   * @default false
   */
  required: booleanAttribute(),
  /**
   * Hint for the browser's autofill, using the values of the HTML `autocomplete` attribute
   * (`street-address`, `off`, …).
   *
   * @type {String}
   * @default undefined
   */
  autocomplete: {
    type: String,
    default: undefined,
  },
  /**
   * Name of the extra form entry carrying the text direction (`ltr` / `rtl`) at submit time,
   * alongside the entry named by `name`.
   *
   * @type {String}
   * @default undefined
   */
  dirname: {
    type: String,
    default: undefined,
  },
  /**
   * Message the browser shows when a required field is submitted empty. Names what this
   * particular field is for; the generic wording comes from `frontend.forms`.
   *
   * @type {String}
   * @default undefined
   */
  requiredMessage: {
    type: String,
    default: undefined,
  },
  /**
   * Marks the field invalid with this message, the way `setCustomValidity()` does on a
   * native control. Clear it to let the field's own constraints decide.
   *
   * @type {String}
   * @default undefined
   */
  customValidity: {
    type: String,
    default: undefined,
  },
  /**
   * Vertical spacing of the field chrome.
   *
   * @type {String}
   * @default "default"
   * @values default, compact, comfortable
   */
  density: {
    type: String,
    validator: (value) => ["default", "compact", "comfortable"].includes(value),
    default: "default",
  },
  /**
   * Compact density on small screens (mobile). A deployment that shrinks its controls on
   * phones turns it on for every element that declares it through `sp_components.yml`.
   *
   * @type {Boolean|String}
   * @default false
   */
  densitysmcompact: booleanAttribute(),
  /**
   * Visual treatment of the field chrome.
   *
   * @type {String}
   * @default "default"
   * @values default, outlined, plain
   */
  variant: {
    type: String,
    validator: (value) => ["default", "outlined", "plain"].includes(value),
    default: "default",
  },
  /**
   * Space-separated ids of the elements that label the field, resolved in the tree the
   * element itself sits in. Set it as `aria-labelledby` on the element. Where the browser
   * lacks ARIA element reflection the referenced text is captured at render time, so a
   * label whose text changes afterwards needs `aria-label`.
   *
   * @type {String}
   * @default undefined
   */
  ariaLabelledby: {
    type: String,
    default: undefined,
  },
  /**
   * Sets a `data-testid` on the inner `<textarea>` element.
   *
   * @type {String}
   * @default undefined
   */
  inputTestId: {
    type: String,
    default: undefined,
  },
});
